import React, { useEffect, useMemo, useRef } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Grid,
  Box,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Slide,
  AppBar,
  Toolbar,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { PersonPinCircleOutlined } from '@mui/icons-material';

import SoftBox from 'components/SoftBox';
import logo from 'assets/images/Anser.png';
import cartoonImage from 'assets/images/Login-amico.svg';
import { useAuth } from 'context/AuthContext';
import { useSystemTheme } from 'context/SystemThemeContext';

import { useAuthFlow } from './hooks/useAuthFlow';
import { useInspection } from './hooks/useInspection';
import { useSession } from './hooks/useSession';
import EmailStep from './components/EmailStep';
import OtpStep from './components/OtpStep';
import PasswordStep from './components/PasswordStep';
import SignupForm from './components/SignupForm';
import RegistrationForm from './components/RegistrationForm';
import ForgotPasswordStep from './components/ForgotPasswordStep';
import ProfileCard from './components/ProfileCard';
import InspectionDialog from './components/InspectionDialog';

import { AUTH_STEPS, STORAGE_KEYS } from './constants';
import { isSuperAdmin } from './utils';

const buildStepConfig = (props) => ({
  [AUTH_STEPS.EMAIL]: {
    title: 'Login/Signup to Ansera AI',
    subtitle: 'To access your dashboard',
    showSocialLogin: true,
    render: () => (
      <EmailStep
        email={props.email}
        setEmail={props.setEmail}
        loadingState={props.loadingState}
        onSubmit={props.handleEmailSubmit}
        isDarkMode={props.isDarkMode}
      />
    ),
  },
  [AUTH_STEPS.OTP]: {
    title: 'Verify Your Email',
    subtitle: 'Enter the verification code we sent to your email',
    render: () => (
      <OtpStep
        email={props.email}
        otp={props.otp}
        setOtp={props.setOtp}
        loadingState={props.loadingState}
        onSubmit={props.handleOtpSubmit}
        onBack={props.resetAuthFlow}
        isDarkMode={props.isDarkMode}
      />
    ),
  },
  [AUTH_STEPS.PASSWORD]: {
    title: 'Welcome Back',
    subtitle: 'Enter your password to continue',
    render: () => (
      <PasswordStep
        email={props.email}
        password={props.password}
        setPassword={props.setPassword}
        rememberMe={props.rememberMe}
        setRememberMe={props.setRememberMe}
        loadingState={props.loadingState}
        onSubmit={props.handlePasswordLogin}
        onForgotClick={() => props.setCurrentStep(AUTH_STEPS.FORGOT_PASSWORD)}
        onBack={props.resetAuthFlow}
        isDarkMode={props.isDarkMode}
      />
    ),
  },
  [AUTH_STEPS.FORGOT_PASSWORD]: {
    title: 'Forgot Password',
    subtitle: "Enter your email and we'll send a reset link",
    render: () => (
      <ForgotPasswordStep
        isDarkMode={props.isDarkMode}
        loadingState={props.loadingState}
        onSubmit={props.handleForgotPassword}
        onBack={() => props.setCurrentStep(AUTH_STEPS.PASSWORD)}
      />
    ),
  },
  [AUTH_STEPS.SIGNUP]: {
    title: 'Complete Your Signup',
    subtitle: 'Set up your password to complete registration',
    render: () => (
      <SignupForm
        email={props.email}
        isDarkMode={props.isDarkMode}
        loadingState={props.loadingState}
        onSubmit={props.handleSignupSubmit}
        showSnackbar={props.showSnackbar}
      />
    ),
  },
  [AUTH_STEPS.REGISTRATION]: {
    title: 'Domain Registration',
    subtitle: 'Add your domain details to complete registration',
    render: () => (
      <RegistrationForm
        email={props.email}
        isDarkMode={props.isDarkMode}
        loadingState={props.loadingState}
        onSubmit={props.handleRegistrationSubmit}
        showSnackbar={props.showSnackbar}
      />
    ),
  },
});


const Auth = () => {
  const { mode } = useSystemTheme();
  const isDarkMode = mode === 'dark';

  const navigate = useNavigate();
  const { user, isAuthenticated, getDomains,isInitialized,loading } = useAuth();
  const { referralCode } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  const inspectionProcessedRef = useRef(false);
  const redirectProcessedRef = useRef(false);

  const authFlow = useAuthFlow();
  const {
    currentStep,
    setCurrentStep,
    loadingState,
    email,
    setEmail,
    otp,
    setOtp,
    password,
    setPassword,
    rememberMe,
    setRememberMe,
    snackbar,
    showSnackbar,
    hideSnackbar,
    resetAuthFlow,
    handleEmailSubmit,
    handleOtpSubmit,
    handlePasswordLogin,
    handleRegistrationSubmit,
    handleSignupSubmit,
    handleGoogleSignIn,
    handleForgotPassword,
  } = authFlow;

  const {
    isInspectionMode,
    isSwitchingUser,
    handleInspectionFromUrl,
  } = useInspection();

  const { initializeSession } = useSession();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  useEffect(() => {
    const finalReferralCode = referralCode || queryParams.get('referral_code');
    if (finalReferralCode) {
      localStorage.setItem(STORAGE_KEYS.REFERRAL_CODE, finalReferralCode);
    } else {
      localStorage.removeItem(STORAGE_KEYS.REFERRAL_CODE);
    }

    const source = queryParams.get('source');
    if (source === 'hubspot_marketplace') {
      localStorage.setItem(STORAGE_KEYS.INSTALL_SOURCE, JSON.stringify({ value: 'hubspot_marketplace', timestamp: Date.now() }));
    }

    const secret = queryParams.get('secret');
    if (secret) localStorage.setItem('secret', secret);

    const domainId = queryParams.get('domain_id');
    if (domainId) localStorage.setItem('domain_id', domainId);

    const clientId = queryParams.get('client_id');
    const redirectUri = queryParams.get('redirect_uri');
    if (clientId && redirectUri) {
      sessionStorage.setItem(
        'pending_oauth',
        JSON.stringify({
          client_id: clientId,
          redirect_uri: redirectUri,
          response_type: queryParams.get('response_type'),
          scope: queryParams.get('scope'),
          state: queryParams.get('state'),
          code_challenge: queryParams.get('code_challenge'),
          code_challenge_method: queryParams.get('code_challenge_method'),
        }),
      );
    }
  }, [location.search, referralCode, queryParams]);


useEffect(() => {
  if (!isInitialized || loading) return;

  if (!isAuthenticated() || !user) return;

  const hasAuthParams = ['secret', 'domain_id', 'role', 'id', 'inspect']
    .some((p) => queryParams.get(p));

  const hasPendingOAuth = !!sessionStorage.getItem('pending_oauth');
  const hasOAuthError = sessionStorage.getItem("oauth_error") === "true";

  if (
    hasAuthParams ||
    isInspectionMode ||
    hasPendingOAuth ||
    hasOAuthError
  ) {
    return;
  }

  if (
    currentStep !== AUTH_STEPS.EMAIL ||
    redirectProcessedRef.current
  ) {
    return;
  }

  redirectProcessedRef.current = true;

  const domains = getDomains();
  const hasDomains = domains?.length > 0;

  if (isSuperAdmin(user?.is_super_admin)) {
    navigate('/customers');
  } else if (hasDomains && domains[0].role === 'read only') {
    navigate('/dashboard');
  } else if (hasDomains) {
    navigate('/settings');
  }

}, [
  isInitialized,
  loading,
  isAuthenticated,
  user,
  queryParams,
  navigate,
  currentStep,
  getDomains,
  isInspectionMode
]);


  useEffect(() => {
    const inspectEmail = queryParams.get('inspect');
    if (!inspectEmail || inspectionProcessedRef.current) return;
    const urlParams = new URLSearchParams(location.search);
    urlParams.delete('inspect');
    const newSearch = urlParams.toString();
    window.history.replaceState({}, '', window.location.pathname + (newSearch ? `?${newSearch}` : ''));

    inspectionProcessedRef.current = true;

    handleInspectionFromUrl(inspectEmail).catch((error) => {
      console.error('Inspection error:', error);
      showSnackbar('Failed to start inspection mode', 'error');
      inspectionProcessedRef.current = false;
    });
  }, [queryParams, location.search, handleInspectionFromUrl, showSnackbar]);

  const stepConfig = useMemo(() => buildStepConfig({
    email, setEmail, otp, setOtp, password, setPassword,
    rememberMe, setRememberMe, loadingState, isDarkMode,
    resetAuthFlow, setCurrentStep, showSnackbar,
    handleEmailSubmit, handleOtpSubmit, handlePasswordLogin,
    handleForgotPassword, handleSignupSubmit, handleRegistrationSubmit,
  }), [
    email, setEmail, otp, setOtp, password, setPassword,
    rememberMe, setRememberMe, loadingState, isDarkMode,
    resetAuthFlow, setCurrentStep, showSnackbar,
    handleEmailSubmit, handleOtpSubmit, handlePasswordLogin,
    handleForgotPassword, handleSignupSubmit, handleRegistrationSubmit,
  ]);

  const activeStep = stepConfig[currentStep] ?? stepConfig[AUTH_STEPS.EMAIL];
  const showSocialLogin = !!activeStep.showSocialLogin;
  const showProfileCard = currentStep === AUTH_STEPS.REGISTRATION || currentStep === AUTH_STEPS.SIGNUP;

  return (
    <>
      <AppBar
        position="static"
        color="inherit"
        sx={{
          boxShadow: 'none',
          backgroundColor: isDarkMode ? '#060C18' : 'white',
          borderBottom: `1px solid ${isDarkMode ? '#2d2d3f' : '#e0e0e0'}`,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', px: 2 }}>
          <Box display="flex" alignItems="center">
            <img
              src={logo}
              alt="Ansera Logo"
              style={{ height: '3rem', filter: isDarkMode ? 'brightness(0) invert(1)' : 'none' }}
            />
          </Box>
          {showProfileCard && <ProfileCard isDarkMode={isDarkMode} onLogout={resetAuthFlow} />}
        </Toolbar>
      </AppBar>

      {isSwitchingUser && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            backgroundColor: isDarkMode ? 'rgba(6,12,24,0.92)' : 'rgba(255,255,255,0.92)',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 3,
          }}
        >
          <Box sx={{ position: 'relative', width: 72, height: 72 }}>
            <CircularProgress size={72} thickness={2} sx={{ color: '#219297', position: 'absolute', top: 0, left: 0 }} />
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <PersonPinCircleOutlined sx={{ fontSize: 28, color: '#219297' }} />
            </Box>
          </Box>
          <Box textAlign="center">
            <Typography variant="h6" fontWeight={600} sx={{ color: isDarkMode ? '#e2e8f0' : '#344767', mb: 0.5 }}>
              Logging in...
            </Typography>
            <Typography variant="body2" sx={{ color: isDarkMode ? '#a0aec0' : '#67748e' }}>
              Please wait while we switch your session
            </Typography>
          </Box>
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        TransitionComponent={(props) => <Slide {...props} direction="down" />}
      >
        <Alert
          onClose={hideSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: '100%',
            color: '#fff',
            backgroundColor: {
              success: '#219297',
              error: '#d32f2f',
              warning: '#ed6c02',
              info: '#0288d1',
            }[snackbar.severity],
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <SoftBox
        sx={{
          minHeight: 'calc(100dvh - 64px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgColor: isDarkMode ? '#060C18' : '#f0f4f7',
          px: 2,
          py: { xs: 2, sm: 2 },
          overflowY: 'auto',
          transition: 'background-color 0.3s ease',
        }}
      >
        <Grid container spacing={4} alignItems="center" justifyContent="center" maxWidth="xl">
          {!isMobile && showSocialLogin && (
            <Grid item md={6} lg={6} xl={6} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Box
                component="img"
                src={cartoonImage}
                alt="Login Illustration"
                sx={{ width: '100%', maxWidth: { md: '350px', lg: '450px', xl: '550px' }, mx: 'auto' }}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={10} md={6} lg={6} xl={6}>
            <Card
              sx={{
                p: { xs: 3, sm: 4 },
                maxWidth: 480,
                mx: 'auto',
                boxShadow: isDarkMode ? '0 10px 30px rgba(0,0,0,0.3)' : '0 10px 30px rgba(0,0,0,0.05)',
                borderRadius: 4,
                backgroundColor: isDarkMode ? '#060C18' : '#fff',
                transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
              }}
            >
              <Grid container direction="column" spacing={3} alignItems="center">
                <Grid item>
                  <Typography
                    fontWeight="bold"
                    align="center"
                    sx={{ fontSize: 'clamp(1.4rem, 2.5vw, 2rem)', color: isDarkMode ? '#e2e8f0' : 'text.primary' }}
                  >
                    {activeStep.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    align="center"
                    sx={{ fontSize: 'clamp(0.8rem, 1.2vw, 1rem)', color: isDarkMode ? '#a0aec0' : 'text.secondary' }}
                  >
                    {activeStep.subtitle}
                  </Typography>
                </Grid>

                <Grid item sx={{ width: '100%' }}>
                  {activeStep.render()}
                </Grid>

                {showSocialLogin && (
                  <>
                    <Grid item>
                      <Typography
                        variant="body2"
                        align="center"
                        sx={{ fontSize: 'clamp(0.8rem, 1vw, 1rem)', color: isDarkMode ? '#a0aec0' : 'text.secondary' }}
                      >
                        OR
                      </Typography>
                    </Grid>
                    <Grid item>
                      <GoogleLogin
                        theme="filled_blue"
                        text="continue_with"
                        onSuccess={handleGoogleSignIn}
                        onError={() => console.log('Google login failed')}
                      />
                      {loadingState === 'google' && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                          <CircularProgress size={24} sx={{ color: '#219297' }} />
                          <Typography variant="body2" sx={{ ml: 1, color: isDarkMode ? '#e2e8f0' : undefined }}>
                            Signing in with Google...
                          </Typography>
                        </Box>
                      )}
                      <Box width="100%" display="flex" justifyContent="center" alignItems="center" sx={{ mt: 1 }}>
                        <Box display="flex" alignItems="center">
                          <input
                            type="checkbox"
                            id="rememberMe"
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                            style={{ width: '16px', height: '16px', marginRight: '8px', cursor: 'pointer', accentColor: '#219297' }}
                          />
                          <Typography
                            component="label"
                            htmlFor="rememberMe"
                            sx={{ fontSize: '13px', color: isDarkMode ? '#e2e8f0' : '#374151', cursor: 'pointer', userSelect: 'none' }}
                          >
                            Keep me signed in
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </>
                )}
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </>
  );
};

export default Auth;